/**
 * Shared options for the admin invoice create + edit forms — the payment-method
 * text and the invoice currencies, both grouped for the dropdowns.
 *
 * MIRROR of backend/src/game/admin/invoice-payment-methods.ts, which validates
 * every method an invoice is saved with; the form loads the list from the
 * server and falls back to this copy. Invoices print the text, never a logo.
 * Same format as Dominion's admin invoice options.
 */

/** Text name for every payment logo file. */
export const LOGO_TEXT: Record<string, string> = {
  // Cards
  'visa': 'Credit card (Visa)', 'mastercard': 'Credit card (Mastercard)', 'amex': 'Credit card (American Express)',
  'maestro': 'Debit card (Maestro)', 'discover': 'Credit card (Discover)', 'diners-club': 'Credit card (Diners Club)',
  'jcb': 'Credit card (JCB)', 'unionpay': 'Credit card (UnionPay)', 'v-pay': 'Debit card (V Pay)',
  'cartes-bancaires': 'Credit card (Cartes Bancaires)', 'dankort': 'Debit card (Dankort)', 'bancomat': 'Debit card (Bancomat)',
  // Digital wallets
  'paypal': 'PayPal', 'apple-pay': 'Apple Pay', 'google-pay': 'Google Pay', 'amazon-pay': 'Amazon Pay',
  'shopify-pay': 'Shop Pay', 'cashapp': 'Cash App', 'venmo': 'Venmo', 'revolut': 'Revolut Pay', 'wise': 'Wise',
  // Buy now, pay later
  'klarna': 'Klarna', 'clearpay': 'Clearpay', 'alma': 'Alma', 'cofidis': 'Cofidis', 'floa': 'Floa',
  'oney': 'Oney', 'scalapay': 'Scalapay', 'zilch': 'Zilch', 'twisto': 'Twisto',
  // Bank / redirect
  'ideal': 'iDEAL', 'bancontact': 'Bancontact', 'eps': 'EPS', 'giropay': 'Giropay', 'przelewy24': 'Przelewy24',
  'blik': 'BLIK', 'trustly': 'Trustly', 'sofort': 'Sofort', 'sepa': 'SEPA bank transfer',
  'sepa-direct-debit': 'SEPA Direct Debit', 'bacs-direct-debit': 'Bacs Direct Debit', 'bank-generic': 'Bank transfer',
  'online-banking': 'Online banking', 'pay-by-bank': 'Pay by Bank', 'multibanco': 'Multibanco', 'mybank': 'MyBank',
  'iris': 'IRIS', 'interac': 'Interac', 'mbway': 'MB WAY', 'postfinance': 'PostFinance', 'truelayer': 'TrueLayer',
  'bizum': 'Bizum',
  // E-wallets
  'skrill': 'Skrill', 'neteller': 'Neteller', 'paysafecard': 'Paysafecard', 'payoneer': 'Payoneer',
  'paysera': 'Paysera', 'payu': 'PayU', 'alipay': 'Alipay', 'wechat-pay': 'WeChat Pay',
  // Nordic / Swiss
  'mobilepay': 'MobilePay', 'swish': 'Swish', 'vipps': 'Vipps', 'siirto': 'Siirto', 'twint': 'TWINT', 'wero': 'Wero',
  // Regional banks
  'citadele': 'Citadele', 'lhv': 'LHV', 'seb': 'SEB', 'swedbank': 'Swedbank', 'otp-bank': 'OTP Bank',
  'csob': 'ČSOB', 'vub': 'VÚB', 'epay-bg': 'ePay.bg', 'simplepay': 'SimplePay',
  // Processors
  'stripe': 'Stripe', 'adyen': 'Adyen', 'brite': 'Brite', 'ivy': 'Ivy', 'volt': 'Volt', 'barion': 'Barion',
  'satispay': 'Satispay', 'nexi': 'Nexi', 'postepay': 'Postepay', 'bancomat-pay': 'Bancomat Pay', 'payconiq': 'Payconiq',
};

/** Logo files that are security badges, not a way to pay. */
export const SECURITY_BADGE_LOGOS = ['3d-secure', 'pci-dss'];

const L = (...slugs: string[]) => slugs.map((slug) => LOGO_TEXT[slug]);

/** Invoice payment-method text, grouped for the dropdown: every logo plus common combinations. */
export const PAYMENT_METHOD_GROUPS: { label: string; methods: string[] }[] = [
  { label: 'Card', methods: [
    'Credit card',
    ...L('visa', 'mastercard', 'amex', 'discover', 'diners-club', 'jcb', 'unionpay', 'cartes-bancaires'),
    'Credit card (Visa / Mastercard)',
    'Debit card', 'Debit card (Visa)', 'Debit card (Mastercard)',
    ...L('maestro', 'v-pay', 'dankort', 'bancomat'),
    'Card payout', 'Card payout (Visa)', 'Card payout (Mastercard)',
  ] },
  { label: 'Digital wallets', methods: [
    ...L('apple-pay'), 'Apple Pay (Visa)', 'Apple Pay (Mastercard)',
    ...L('google-pay'), 'Google Pay (Visa)', 'Google Pay (Mastercard)',
    ...L('paypal', 'amazon-pay', 'shopify-pay', 'cashapp', 'venmo', 'revolut', 'wise'),
  ] },
  { label: 'SEPA / bank', methods: [
    ...L('sepa'), 'SEPA Instant transfer', 'SEPA payout', ...L('sepa-direct-debit'),
    ...L('bank-generic', 'online-banking', 'pay-by-bank'), 'Open Banking',
    ...L('bacs-direct-debit', 'truelayer'),
  ] },
  { label: 'Local payment methods', methods: [
    ...L('bancontact'), 'Bancontact (card)', 'Bancontact (app)',
    ...L('blik', 'ideal', 'eps', 'giropay', 'przelewy24', 'trustly', 'sofort', 'multibanco', 'mybank',
      'iris', 'interac', 'mbway', 'postfinance', 'bizum'),
  ] },
  { label: 'Buy now, pay later', methods: L('klarna', 'clearpay', 'alma', 'cofidis', 'floa', 'oney', 'scalapay', 'zilch', 'twisto') },
  { label: 'E-wallets', methods: L('skrill', 'neteller', 'paysafecard', 'payoneer', 'paysera', 'payu', 'alipay', 'wechat-pay') },
  { label: 'Nordic / Swiss', methods: L('mobilepay', 'swish', 'vipps', 'siirto', 'twint', 'wero') },
  { label: 'Regional banks', methods: L('citadele', 'lhv', 'seb', 'swedbank', 'otp-bank', 'csob', 'vub', 'epay-bg', 'simplepay') },
  { label: 'Processors', methods: L('stripe', 'adyen', 'brite', 'ivy', 'volt', 'barion', 'satispay', 'nexi', 'postepay', 'bancomat-pay', 'payconiq') },
];

export const ALL_PAYMENT_METHODS: string[] = PAYMENT_METHOD_GROUPS.flatMap((g) => g.methods);

/**
 * Every currency in use in an EU member state: the euro plus the six national
 * currencies still in circulation (no BGN — Bulgaria has used the euro since
 * 2026-01-01). Mirrors the server's EU_CURRENCIES.
 */
export const EU_CURRENCIES = ['EUR', 'CZK', 'DKK', 'HUF', 'PLN', 'RON', 'SEK'];

/** Non-EU currencies an invoice may also be issued in. */
export const OTHER_INVOICE_CURRENCIES = ['USD', 'GBP', 'CAD', 'AUD'];

/** The currency dropdown's two groups (generate and edit). Mirrors the server. */
export const INVOICE_CURRENCY_GROUPS: { label: string; currencies: string[] }[] = [
  { label: 'EU currencies', currencies: EU_CURRENCIES },
  { label: 'Other currencies', currencies: OTHER_INVOICE_CURRENCIES },
];

/** Dominion's names for the same lists (server: EU_/OTHER_INVOICE_CURRENCIES, INVOICE_CURRENCIES). */
export const EU_INVOICE_CURRENCIES = EU_CURRENCIES;
export const INVOICE_CURRENCIES: string[] = [...EU_CURRENCIES, ...OTHER_INVOICE_CURRENCIES];

/** One of the 11 invoice currencies, case-insensitive. Mirrors the server's isInvoiceCurrency. */
export function isInvoiceCurrency(v: unknown): v is string {
  return typeof v === 'string' && INVOICE_CURRENCIES.includes(v.trim().toUpperCase());
}

/**
 * Statuses an operator may set. `value` is what the API takes, `stored` what the
 * list returns (the database enum), `label` what the panel shows.
 */
export const INVOICE_STATUSES: { value: 'paid' | 'pending' | 'not_paid' | 'cancelled' | 'refunded'; stored: string; label: string }[] = [
  { value: 'paid', stored: 'PAID', label: 'PAID' },
  { value: 'pending', stored: 'PENDING', label: 'PENDING' },
  { value: 'not_paid', stored: 'NOT_PAID', label: 'NOT PAID' },
  { value: 'cancelled', stored: 'CANCELLED', label: 'CANCELLED' },
  { value: 'refunded', stored: 'REFUNDED', label: 'REFUNDED' },
];

/** A stored status as the panel prints it: NOT_PAID → NOT PAID. */
export function statusLabel(stored: string): string {
  return INVOICE_STATUSES.find((s) => s.stored === stored)?.label ?? stored.replace(/_/g, ' ');
}

/** VAT rates offered in the generator and the editor, in percent (EU standard + reduced rates). */
export const VAT_RATES = [0, 5, 9, 12, 17, 19, 20, 21, 22, 23, 24, 25, 27];

/** Item code when nothing more specific applies. */
export const ITEM_CODE_MANUAL = 'TRB-MANUAL';

/** Item code for a Shroom amount: TRB-SHRM-00500, or TRB-SHRM-CSTM when none is given. */
export function shroomItemCode(shrooms: number): string {
  return shrooms > 0 ? `TRB-SHRM-${String(Math.floor(shrooms)).padStart(5, '0')}` : 'TRB-SHRM-CSTM';
}

/**
 * Amount excl. VAT ⇄ incl. VAT, in minor units, with the server's VAT rule
 * (vat = round(subtotal × rate / 100)). `rate` is a percentage.
 */
export function inclFromExcl(exclCents: number, rate: number): number {
  return exclCents + Math.round(exclCents * rate / 100);
}
export function exclFromIncl(inclCents: number, rate: number): number {
  return Math.round(inclCents / (1 + rate / 100));
}

/** The live preview under the generator: Subtotal · VAT (n%) · Total. */
export function totalsLine(exclCents: number, rate: number, currency: string): string {
  const vat = Math.round(exclCents * rate / 100);
  const f = (c: number) => `${(c / 100).toFixed(2)} ${currency}`;
  return `Subtotal: ${f(exclCents)} · VAT (${rate}%): ${f(vat)} · Total: ${f(exclCents + vat)}`;
}

/**
 * Shrooms from money, EUR only: floor(amount incl. VAT × Shrooms per euro). For
 * any other currency the operator types the Shrooms — no live FX rate is used.
 */
export function shroomsFromEur(inclCents: number, shroomsPerEur: number): number {
  return Math.floor((inclCents / 100) * shroomsPerEur);
}

/** Price incl. VAT (cents, EUR) of a Shroom amount at the configured Shrooms per euro. */
export function eurCentsForShrooms(shrooms: number, shroomsPerEur: number): number {
  return shroomsPerEur > 0 ? Math.round((shrooms / shroomsPerEur) * 100) : 0;
}

/**
 * Card number field (§8). Mirrors the server's masked-pan rule: at most the
 * first 6 and last 4 digits visible. Blank = no "Card number" row on the PDF.
 * The input allows room for spaces/dashes, which the server strips.
 */
export const MASKED_PAN_INPUT_MAX = 25;
export const MASKED_PAN_PLACEHOLDER = '411111******1111';
export const MASKED_PAN_HINT = 'Optional. First 6 and last 4 digits at most, middle masked with *. Leave blank to omit it from the PDF.';

/**
 * Free-text payment details (IBAN, bank, account holder — anything), printed on
 * the PDF exactly as typed, line breaks kept. Full card numbers and IBANs are refused
 * by the server (they are only ever sent to the payment provider). Blank = no row.
 */
export const PAYMENT_DETAILS_PLACEHOLDER = 'e.g. IBAN LV80 •••• 5001\nBIC HABALV22';
export const PAYMENT_DETAILS_HINT = 'Optional. Any payment information — printed on the invoice as typed. Never a full card number or IBAN: mask it (e.g. LV80 •••• 5001). Leave blank to omit it.';
